import { Injectable, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { captureException } from '@sentry/angular';
import { AccountService } from '@wilson/account';
import { ShiftOptimizerGatewayService } from '@wilson/api/gateway';
import {
  AbsenceWithDecisionAndHighlighted,
  AssignmentData,
  AssignmentOptimizerJobResult,
  AssignmentOptimizerJobSuccessResponse,
  AssignmentOptimizerKPIResponse,
  PublicationStatus,
  Shift,
  TimelineTimeframe,
  TimelineUserDTO,
  UnassignedShift,
  UserTimelines,
} from '@wilson/interfaces';
import { WilsonState } from '@wilson/non-domain-specific/decorators/wilson-state';
import { NzMessageService } from 'ng-zorro-antd/message';
import {
  Observable,
  catchError,
  combineLatest,
  finalize,
  firstValueFrom,
  map,
  of,
  switchMap,
} from 'rxjs';
import { getFilteredUsersWithUpdatedShifts } from './filtered-users-with-updated-shifts.fn';
import {
  ChangedShiftRecord,
  generateRecord,
} from './generate-updated-record.fn';
import { shiftIdToUserIdMap } from './shift-id-to-user-id-map.fn';
import { ShiftOptimizerDataHelperService } from './shift-optimizer-data-helper.service';

@Injectable()
export class ShiftTimelineDraftHelperService {
  private readonly accountService = inject(AccountService);
  private readonly shiftOptimizerDataHelperService = inject(
    ShiftOptimizerDataHelperService,
  );
  private readonly nzMessageService = inject(NzMessageService);
  private readonly translate = inject(TranslateService);
  private shiftOptimizerGatewayService = inject(ShiftOptimizerGatewayService);

  @WilsonState<Map<string, string[]>>(new Map<string, string[]>())
  private trackedAssignmentsMap!: Map<string, string[]>;

  @WilsonState<Record<string, ChangedShiftRecord<Shift>>>({})
  private changedShiftRecords!: Record<string, ChangedShiftRecord<Shift>>;
  public changedShiftRecords$!: Observable<
    Record<string, ChangedShiftRecord<Shift>>
  >;
  public changedShiftIds$ = this.changedShiftRecords$.pipe(
    map((changedRecords) => Object.keys(changedRecords)),
  );

  @WilsonState<string>('')
  private searchTerm!: string;
  private searchTerm$!: Observable<string>;

  @WilsonState<TimelineTimeframe>({ start: 0, end: 0 })
  private dateRange!: TimelineTimeframe;
  public dateRange$!: Observable<TimelineTimeframe>;

  @WilsonState<UserTimelines[]>([])
  private userTimelines!: UserTimelines[];
  private userTimelines$!: Observable<UserTimelines[]>;

  @WilsonState<UnassignedShift[]>([])
  private unassignedShifts!: UnassignedShift[];
  public unassignedShifts$!: Observable<UnassignedShift[]>;

  @WilsonState<Record<string, string[]>>({})
  private originalUserIdToShiftIdsMap!: Record<string, string[]>;

  @WilsonState<AssignmentOptimizerJobResult | null>(null)
  private assignmentOptimizerResult!: AssignmentOptimizerJobResult;

  @WilsonState<{
    [userId: string]: string[];
  }>({})
  private originalAbsenceAssignments!: {
    [userId: string]: string[];
  };

  @WilsonState<string>('')
  private jobId!: string;

  @WilsonState<string[]>([])
  private timelineUserIds!: string[];

  @WilsonState<boolean>(false)
  private isCellView!: boolean;
  public isCellView$!: Observable<boolean>;

  @WilsonState<UserTimelines[]>([])
  private userTimelineAssignments!: UserTimelines[];
  private userTimelineAssignments$!: Observable<UserTimelines[]>;

  @WilsonState<boolean>(false)
  isLoadingAssignmentOptimizerKpi!: boolean;
  isLoadingAssignmentOptimizerKpi$!: Observable<boolean>;

  public assignmentOptimizerKpi$!: Observable<AssignmentOptimizerKPIResponse>;

  public assignments$ = combineLatest([
    this.userTimelineAssignments$,
    this.searchTerm$,
  ]).pipe(
    map(([users, searchTerm]) => {
      return users.filter((u) => {
        return (
          u.firstName.toLocaleLowerCase().includes(searchTerm) ||
          u.lastName.toLocaleLowerCase().includes(searchTerm)
        );
      });
    }),
  );

  private shiftIdToShiftMap$ = combineLatest([
    this.unassignedShifts$,
    this.userTimelines$,
  ]).pipe(
    map(([unassignedShifts, userTimelines]) =>
      this.createShiftMap({
        unassignedShifts,
        userTimelines,
      }),
    ),
  );

  public async setShiftAssignmentOptimizerJobResponse(
    shiftOptimizerJob: AssignmentOptimizerJobSuccessResponse,
  ): Promise<void> {
    const timelineSettings = JSON.parse(
      shiftOptimizerJob.originalInput.timelineSettings,
    );
    this.isCellView = timelineSettings.isCellView;

    this.dateRange = {
      start: new Date(shiftOptimizerJob.originalInput.startDateTime).getTime(),
      end: new Date(shiftOptimizerJob.originalInput.endDateTime).getTime(),
    };
    this.originalUserIdToShiftIdsMap =
      shiftOptimizerJob.originalInput.shiftAssignments;
    this.originalUserIdToShiftIdsMap['null'] =
      shiftOptimizerJob.originalInput.unassignedShifts;

    this.assignmentOptimizerResult = shiftOptimizerJob.result;
    this.originalAbsenceAssignments =
      shiftOptimizerJob.originalInput.absenceAssignments;

    this.jobId = shiftOptimizerJob.originalInput.jobId;
    this.unassignedShifts =
      await this.shiftOptimizerDataHelperService.getUnassignedShifts(
        shiftOptimizerJob.result.unassignedShifts,
      );
    await this.getUserTimelines(
      shiftOptimizerJob.result.shiftAssignments,
      shiftOptimizerJob.originalInput.absenceAssignments,
    );
    await this.setShiftAssignment(shiftOptimizerJob.result.shiftAssignments);
  }

  private async getUserTimelines(
    shiftAssignments: Record<string, string[]>,
    absenceAssignments: Record<string, string[]>,
  ): Promise<void> {
    const timelineUserIds = Object.keys(shiftAssignments);
    this.timelineUserIds = timelineUserIds;
    this.userTimelines = await firstValueFrom(
      this.accountService.getTimelineUsers(timelineUserIds).pipe(
        map((users) => this.sortUserByName(users)),
        switchMap((users) =>
          combineLatest(
            users.map((user) =>
              this.shiftOptimizerDataHelperService.generateUserTimelinesWithHomeLocation(
                user,
              ),
            ),
          ),
        ),
        catchError((error) => {
          console.error(
            'An error occurred while fetching user timelines:',
            error,
          );
          captureException(error);
          this.nzMessageService.error(
            this.translate.instant('general.something_went_wrong'),
          );
          return of([]);
        }),
      ),
    );
    const allShiftIds: string[] = Object.values(shiftAssignments).flat();
    const allAbsenceIds: string[] = Object.values(absenceAssignments).flat();

    const shifts =
      await this.shiftOptimizerDataHelperService.fetchShifts(allShiftIds);
    shifts.map((shift) => {
      const userId = Object.keys(shiftAssignments).find((userId) =>
        shiftAssignments[userId].includes(shift.id),
      );
      if (userId) {
        shift.userId = userId;
      }
      return shift;
    });

    const absences: AbsenceWithDecisionAndHighlighted[] = [];
    for await (const batch of this.shiftOptimizerDataHelperService.fetchAbsences(
      {
        start: new Date(this.dateRange.start),
        end: new Date(this.dateRange.end),
      },
      allAbsenceIds,
    )) {
      absences.push(...batch);
    }

    this.userTimelines.forEach((user) => {
      this.shiftOptimizerDataHelperService.populateUserAssignments(
        user,
        shifts,
        absences,
        this.isCellView,
      );
    });
  }

  private async setShiftAssignment(
    shiftAssignments: Record<string, string[]>,
  ): Promise<void> {
    const shiftIdToShiftMap = await firstValueFrom(this.shiftIdToShiftMap$);
    const originalShiftIdToUserIdMap = shiftIdToUserIdMap(
      this.originalUserIdToShiftIdsMap,
    );

    this.prepareAssignmentsForDraftMode(shiftIdToShiftMap, shiftAssignments);

    this.changedShiftRecords = this.findChangedShifts(
      shiftIdToShiftMap,
      originalShiftIdToUserIdMap,
    );

    this.updateChangedShiftRecordsPublicationStatus();
  }

  private prepareAssignmentsForDraftMode(
    shiftIdToShiftMap: Map<string, Shift>,
    shiftAssignments: Record<string, string[]>,
  ): void {
    const filteredUsersWithUpdatedShifts = getFilteredUsersWithUpdatedShifts({
      users: this.userTimelines,
      shiftAssignments,
      shiftIdToShiftMap,
    });
    this.createTrackedAssignmentsMap(filteredUsersWithUpdatedShifts);
    this.userTimelineAssignments = filteredUsersWithUpdatedShifts;
  }

  private findChangedShifts(
    shiftIdToShiftMap: Map<string, Shift>,
    originalShiftIdToUserIdMap: Map<string, string | null>,
  ): Record<string, ChangedShiftRecord<Shift>> {
    const changedShiftRecord: Record<string, ChangedShiftRecord<Shift>> = {};

    shiftIdToShiftMap.forEach((shift, shiftId) => {
      const hasChangedAssignment =
        shift.userId !== originalShiftIdToUserIdMap.get(shiftId);

      if (hasChangedAssignment) {
        changedShiftRecord[shiftId] = {
          originalShift: shift,
          newUserId: shift.userId,
        };
      }
    });

    return changedShiftRecord;
  }

  private updateChangedShiftRecordsPublicationStatus(): void {
    const assignments = this.userTimelineAssignments;
    const changedShiftRecords = this.changedShiftRecords;
    const changedShiftIds = Object.keys(changedShiftRecords);

    const updatedAssignments = assignments.map((assignment) => ({
      ...assignment,
      shiftsWithoutActivities: Object.values(
        assignment.shiftsWithoutActivitiesDictionary,
      ).map((shift) => {
        if (changedShiftIds.includes(shift.id)) {
          return {
            ...shift,
            publicationStatus: PublicationStatus.NotPublished,
          };
        } else {
          return shift;
        }
      }),
    }));
    this.userTimelineAssignments = updatedAssignments;
  }

  private createShiftMap({
    unassignedShifts,
    userTimelines,
  }: {
    unassignedShifts: UnassignedShift[];
    userTimelines: UserTimelines[];
  }): Map<string, Shift> {
    const originalShiftsMap = new Map<string, Shift>();

    userTimelines.forEach((userTimeline) => {
      Object.values(userTimeline.shiftsWithoutActivitiesDictionary).forEach(
        (shift) => {
          originalShiftsMap.set(shift.id, shift);
        },
      );
    });

    unassignedShifts.forEach((shift) => {
      originalShiftsMap.set(shift.id, shift);
    });
    return originalShiftsMap;
  }

  private createTrackedAssignmentsMap(users: UserTimelines[]): void {
    const result = new Map<string, string[]>();
    users.map((user) => {
      if (user.id) {
        const shiftIds = Object.values(
          user.shiftsWithoutActivitiesDictionary,
        ).map((shift) => shift.id);

        result.set(user.id, shiftIds);
      }
    });

    this.trackedAssignmentsMap = result;
  }

  public async updateShiftsToUpdateMap(
    shiftId: string,
    userId: string | null,
  ): Promise<void> {
    const shiftIdToShiftMap = await firstValueFrom(this.shiftIdToShiftMap$);

    const newRecords = generateRecord({
      currentRecords: this.changedShiftRecords,
      shiftId,
      userId,
      shiftIdToShiftMap,
      originalUserIdToShiftIdsMap: this.originalUserIdToShiftIdsMap,
    });

    this.changedShiftRecords = newRecords;
  }

  public setSearchTerm(value: string): void {
    this.searchTerm = value;
  }

  public resetSelectedShifts(): void {
    this.changedShiftRecords = {};
  }

  public fetchAssignmentOptimizerKpis(assignmentData: AssignmentData): void {
    this.isLoadingAssignmentOptimizerKpi = true;
    this.assignmentOptimizerKpi$ = this.shiftOptimizerGatewayService
      .getAssignmentKpis(this.jobId, assignmentData)
      .pipe(
        catchError((error) => {
          captureException(error);
          this.nzMessageService.error(
            this.translate.instant('general.something_went_wrong'),
          );
          return of({
            total_kpis: [],
            user_kpis: {},
            free_text_field: '',
          });
        }),
        finalize(() => (this.isLoadingAssignmentOptimizerKpi = false)),
      );
  }

  public updateTrackedAssignmentsMap(
    shiftId: string,
    currentUserId?: string,
    previousUserId?: string,
  ): void {
    const updatedMap = new Map(this.trackedAssignmentsMap);
    const payload: {
      userId: string;
      shiftIds: string[];
    }[] = [];

    if (currentUserId) {
      const currentUserShiftIds = updatedMap.get(currentUserId) || [];
      const updatedShiftIds = [...currentUserShiftIds, shiftId];
      const usersAndShiftIds = {
        userId: currentUserId as string,
        shiftIds: updatedShiftIds,
      };
      updatedMap.set(currentUserId, updatedShiftIds);
      payload.push(usersAndShiftIds);
    }

    if (previousUserId) {
      const previousUserShiftIds = updatedMap.get(previousUserId) || [];
      const updatedPreviousUserShiftIds = previousUserShiftIds.filter(
        (id) => id !== shiftId,
      );
      const usersAndShiftIds = {
        userId: previousUserId as string,
        shiftIds: updatedPreviousUserShiftIds,
      };
      updatedMap.set(previousUserId, updatedPreviousUserShiftIds);
      payload.push(usersAndShiftIds);
    }

    this.trackedAssignmentsMap = updatedMap;
  }

  private sortUserByName(users: TimelineUserDTO[]): TimelineUserDTO[] {
    return users.sort((a, b) =>
      `${a.lastName} ${a.firstName}`.localeCompare(
        `${b.lastName} ${b.firstName}`,
      ),
    );
  }
}
